import { Loader } from './Loader';
import type { FC, ReactNode, SetStateAction } from 'react';
import { createContext, useMemo, useState } from 'react';

type LoaderContextType = {
  isVisible: boolean;
  showLoader: (message?: string) => void;
  hideLoader: () => void;
};

type LoaderContextProvider = {
  children: ReactNode;
  showDefaultLoader?: boolean;
};

export const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

export const LoaderProvider: FC<LoaderContextProvider> = ({ children, showDefaultLoader = true }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [loaderMessage, setLoaderMessage] = useState<string | undefined>();
  const handleBodyOverflow = (loading: boolean) =>
    window.document.body.classList[loading ? 'add' : 'remove']('overflow-hidden');

  const contextValue = useMemo(
    () => ({
      isVisible,
      showLoader: (message: SetStateAction<string>) => {
        setLoaderMessage(message);
        setIsVisible(true);
        handleBodyOverflow(true);
      },
      hideLoader: () => {
        setIsVisible(false);
        handleBodyOverflow(false);
      },
    }),
    [isVisible]
  );

  return (
    <LoaderContext.Provider value={contextValue}>
      {isVisible && showDefaultLoader && <Loader loaderMessage={loaderMessage} />}
      {children}
    </LoaderContext.Provider>
  );
};
